import { useState, useRef, useCallback } from 'react';
import {
  useSentinelDashboard,
  useSentinelStatus,
  useEnrollFace,
  useAccessCheck,
  useVerifyIdentity,
  useIdentifyPerson,
  useCheckLiveness,
  type EnrollRequest,
  type AccessCheckResponse,
  type VerifyResponse,
  type IdentifyResponse,
  type LivenessResponse,
} from '../api/sentinel';

// ── Helpers ──

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data:image/...;base64, prefix
      resolve(result.split(',')[1] || result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

type ActiveSection = 'enroll' | 'access' | 'verify' | 'identify' | 'liveness';

const CLEARANCE_LEVELS = ['visitor', 'employee', 'admin', 'restricted'] as const;

export default function SentinelPage() {
  const { data: dashboard, isLoading: dashLoading } = useSentinelDashboard();
  const { data: status } = useSentinelStatus();

  const [activeSection, setActiveSection] = useState<ActiveSection>('enroll');

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Sentinel Surveillance</h1>
        <p className="text-sm text-gray-400 mt-1">
          Biometric access control, face recognition & anti-spoofing
        </p>
      </div>

      {/* Status Bar */}
      {status && <StatusBar status={status} />}

      {/* Dashboard KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPICard
          label="Cameras actives"
          value={dashLoading ? '--' : `${dashboard?.cameras_active ?? 0}/${dashboard?.cameras_total ?? 0}`}
          color="cyan"
        />
        <KPICard
          label="Tracking actif"
          value={dashLoading ? '--' : String(dashboard?.active_tracking ?? 0)}
          color="purple"
        />
        <KPICard
          label="Zones surveillees"
          value={dashLoading ? '--' : String(dashboard?.zones_monitored ?? 0)}
          color="green"
        />
        <KPICard
          label="Alertes aujourd'hui"
          value={dashLoading ? '--' : String(dashboard?.alerts_today ?? 0)}
          color="amber"
        />
        <KPICard
          label="Personnes enrolees"
          value={dashLoading ? '--' : String(dashboard?.persons_enrolled ?? 0)}
          color="blue"
        />
        <KPICard
          label="Uptime"
          value={status ? formatUptime(status.uptime_seconds) : '--'}
          color="emerald"
        />
      </div>

      {/* Recent Events */}
      {dashboard?.recent_events && dashboard.recent_events.length > 0 && (
        <div className="bg-surface-secondary rounded-xl border border-border-subtle p-5">
          <h3 className="text-sm font-semibold text-gray-300 mb-3">Evenements recents</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {dashboard.recent_events.map((evt) => (
              <div
                key={evt.id}
                className="flex items-center justify-between p-2.5 bg-surface-tertiary rounded-lg border border-border-subtle"
              >
                <div className="flex items-center gap-3">
                  <EventTypeBadge type={evt.type} />
                  <div>
                    <span className="text-sm text-gray-200">{evt.description}</span>
                    {evt.zone && (
                      <span className="ml-2 text-xs text-gray-500">Zone: {evt.zone}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {evt.confidence !== undefined && (
                    <span className="text-xs text-gray-400">
                      {(evt.confidence * 100).toFixed(0)}%
                    </span>
                  )}
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {new Date(evt.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section Tabs */}
      <div className="bg-surface-secondary rounded-xl border border-border-subtle">
        <div className="flex overflow-x-auto border-b border-border-subtle">
          {([
            { key: 'enroll' as const, label: 'Face Enrollment' },
            { key: 'access' as const, label: 'Access Verification' },
            { key: 'verify' as const, label: 'Identity Verification' },
            { key: 'identify' as const, label: 'Identification' },
            { key: 'liveness' as const, label: 'Liveness Check' },
          ]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveSection(tab.key)}
              className={`px-5 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                activeSection === tab.key
                  ? 'text-cyber-cyan border-b-2 border-cyber-cyan'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {activeSection === 'enroll' && <EnrollSection />}
          {activeSection === 'access' && <AccessCheckSection />}
          {activeSection === 'verify' && <VerifySection />}
          {activeSection === 'identify' && <IdentifySection />}
          {activeSection === 'liveness' && <LivenessSection />}
        </div>
      </div>
    </div>
  );
}

// ── Status Bar ──

function StatusBar({ status }: { status: NonNullable<ReturnType<typeof useSentinelStatus>['data']> }) {
  const statusColor =
    status.status === 'operational'
      ? 'bg-green-500'
      : status.status === 'degraded'
        ? 'bg-amber-500'
        : 'bg-red-500';

  const modules = status.modules_biometriques;

  return (
    <div className="bg-surface-secondary rounded-xl border border-border-subtle p-4 flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-2">
        <div className={`w-2.5 h-2.5 rounded-full ${statusColor} animate-pulse`} />
        <span className="text-sm font-medium text-gray-200 capitalize">{status.status}</span>
      </div>
      <div className="h-4 w-px bg-border-subtle" />
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <span>GPU:</span>
        <span className={status.gpu_available ? 'text-green-400' : 'text-red-400'}>
          {status.gpu_available ? 'Available' : 'Unavailable'}
        </span>
      </div>
      <div className="h-4 w-px bg-border-subtle" />
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <span>Model:</span>
        <span className="text-gray-300">{status.model_version}</span>
      </div>
      <div className="h-4 w-px bg-border-subtle" />
      <div className="flex flex-wrap items-center gap-2">
        <ModuleBadge label="Detection" active={modules.face_detection} />
        <ModuleBadge label="Recognition" active={modules.face_recognition} />
        <ModuleBadge label="Liveness" active={modules.liveness_detection} />
        <ModuleBadge label="Anti-spoof" active={modules.anti_spoofing} />
      </div>
    </div>
  );
}

function ModuleBadge({ label, active }: { label: string; active: boolean }) {
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded-full border ${
        active
          ? 'bg-green-500/15 text-green-400 border-green-500/30'
          : 'bg-red-500/15 text-red-400 border-red-500/30'
      }`}
    >
      {label}
    </span>
  );
}

// ── KPI Card ──

const COLOR_MAP: Record<string, string> = {
  cyan: 'border-cyan-500/30 text-cyan-400',
  purple: 'border-purple-500/30 text-purple-400',
  green: 'border-green-500/30 text-green-400',
  amber: 'border-amber-500/30 text-amber-400',
  blue: 'border-blue-500/30 text-blue-400',
  emerald: 'border-emerald-500/30 text-emerald-400',
};

function KPICard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className={`bg-surface-secondary rounded-xl border p-4 ${COLOR_MAP[color] || 'border-border-subtle text-gray-400'}`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-bold mt-1">{value}</div>
    </div>
  );
}

// ── Event Type Badge ──

function EventTypeBadge({ type }: { type: string }) {
  const styles: Record<string, string> = {
    access_granted: 'bg-green-500/15 text-green-400 border-green-500/30',
    access_denied: 'bg-red-500/15 text-red-400 border-red-500/30',
    unknown_person: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    liveness_fail: 'bg-red-500/15 text-red-400 border-red-500/30',
    zone_alert: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  };
  const labels: Record<string, string> = {
    access_granted: 'Granted',
    access_denied: 'Denied',
    unknown_person: 'Unknown',
    liveness_fail: 'Spoof',
    zone_alert: 'Zone',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border ${styles[type] || 'bg-gray-500/15 text-gray-400 border-gray-500/30'}`}>
      {labels[type] || type}
    </span>
  );
}

// ── Image Upload Hook ──

function useImageUpload() {
  const [preview, setPreview] = useState<string | null>(null);
  const [base64, setBase64] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(async (file: File) => {
    const b64 = await fileToBase64(file);
    setBase64(b64);
    setPreview(URL.createObjectURL(file));
  }, []);

  const reset = useCallback(() => {
    setPreview(null);
    setBase64(null);
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  return { preview, base64, inputRef, handleFile, reset };
}

function ImageUploadArea({
  inputRef,
  onFile,
  preview,
}: {
  inputRef: React.RefObject<HTMLInputElement>;
  onFile: (file: File) => void;
  preview: string | null;
}) {
  return (
    <div
      onClick={() => inputRef.current?.click()}
      className="border-2 border-dashed border-border-subtle rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-cyber-cyan/40 transition-colors min-h-[180px]"
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
      />
      {preview ? (
        <img src={preview} alt="Preview" className="max-h-40 rounded-lg object-contain" />
      ) : (
        <>
          <svg
            className="w-10 h-10 text-gray-600 mb-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z"
            />
          </svg>
          <p className="text-sm text-gray-500">Click to upload an image</p>
          <p className="text-xs text-gray-600 mt-1">JPG, PNG or WebP</p>
        </>
      )}
    </div>
  );
}

// ── Spinner ──

function Spinner() {
  return (
    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

// ── Enroll Section ──

function EnrollSection() {
  const enroll = useEnrollFace();
  const { preview, base64, inputRef, handleFile, reset } = useImageUpload();
  const [name, setName] = useState('');
  const [personId, setPersonId] = useState('');
  const [clearance, setClearance] = useState<EnrollRequest['clearance_level']>('employee');

  function handleEnroll() {
    if (!base64 || !name.trim()) return;
    enroll.mutate(
      {
        person_name: name.trim(),
        person_id: personId.trim() || undefined,
        image_base64: base64,
        clearance_level: clearance,
      },
      {
        onSuccess: () => {
          reset();
          setName('');
          setPersonId('');
        },
      }
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-300">Enroll a New Face</h3>
      <p className="text-xs text-gray-500">
        Register a person's face for biometric access control.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ImageUploadArea inputRef={inputRef} onFile={handleFile} preview={preview} />

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Full Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jean Dupont"
              className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-4 py-2.5 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/50"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Person ID (optional)</label>
            <input
              type="text"
              value={personId}
              onChange={(e) => setPersonId(e.target.value)}
              placeholder="EMP-001"
              className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-4 py-2.5 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/50"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Clearance Level</label>
            <div className="flex gap-2">
              {CLEARANCE_LEVELS.map((lvl) => (
                <button
                  key={lvl}
                  onClick={() => setClearance(lvl)}
                  className={`flex-1 px-3 py-2 rounded-lg border transition-all text-xs capitalize ${
                    clearance === lvl
                      ? 'bg-helios-700/30 border-cyber-cyan/40 text-gray-100'
                      : 'bg-surface-tertiary border-border-subtle text-gray-400 hover:text-gray-200'
                  }`}
                >
                  {lvl}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleEnroll}
            disabled={enroll.isPending || !base64 || !name.trim()}
            className="w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyber-cyan to-helios-600 text-white font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {enroll.isPending ? (
              <>
                <Spinner /> Enrolling...
              </>
            ) : (
              'Enroll Face'
            )}
          </button>

          {enroll.isError && (
            <p className="text-xs text-red-400">
              Error: {(enroll.error as Error).message}
            </p>
          )}

          {enroll.isSuccess && enroll.data && (
            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-sm text-green-400">{enroll.data.message}</p>
              <p className="text-xs text-gray-400 mt-1">
                Person ID: <span className="text-gray-300 font-mono">{enroll.data.person_id}</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Access Check Section ──

function AccessCheckSection() {
  const accessCheck = useAccessCheck();
  const { preview, base64, inputRef, handleFile, reset } = useImageUpload();
  const [zoneId, setZoneId] = useState('');
  const [requiredClearance, setRequiredClearance] = useState<'visitor' | 'employee' | 'admin' | 'restricted'>('employee');
  const [result, setResult] = useState<AccessCheckResponse | null>(null);

  function handleCheck() {
    if (!base64) return;
    accessCheck.mutate(
      {
        image_base64: base64,
        zone_id: zoneId.trim() || undefined,
        required_clearance: requiredClearance,
      },
      {
        onSuccess: (res) => setResult(res),
      }
    );
  }

  function handleReset() {
    reset();
    setResult(null);
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-300">Access Verification</h3>
      <p className="text-xs text-gray-500">
        Verify a person's identity and clearance for zone access.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <ImageUploadArea inputRef={inputRef} onFile={handleFile} preview={preview} />
          <div>
            <label className="block text-xs text-gray-400 mb-1">Zone ID (optional)</label>
            <input
              type="text"
              value={zoneId}
              onChange={(e) => setZoneId(e.target.value)}
              placeholder="ZONE-A1"
              className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-4 py-2.5 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/50"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Required Clearance</label>
            <div className="flex gap-2">
              {CLEARANCE_LEVELS.map((lvl) => (
                <button
                  key={lvl}
                  onClick={() => setRequiredClearance(lvl)}
                  className={`flex-1 px-3 py-2 rounded-lg border transition-all text-xs capitalize ${
                    requiredClearance === lvl
                      ? 'bg-helios-700/30 border-cyber-cyan/40 text-gray-100'
                      : 'bg-surface-tertiary border-border-subtle text-gray-400 hover:text-gray-200'
                  }`}
                >
                  {lvl}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCheck}
              disabled={accessCheck.isPending || !base64}
              className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyber-cyan to-helios-600 text-white font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {accessCheck.isPending ? (
                <>
                  <Spinner /> Checking...
                </>
              ) : (
                'Check Access'
              )}
            </button>
            {result && (
              <button
                onClick={handleReset}
                className="px-4 py-2.5 rounded-lg border border-border-subtle text-gray-400 text-sm hover:text-gray-200 transition-colors"
              >
                Reset
              </button>
            )}
          </div>
          {accessCheck.isError && (
            <p className="text-xs text-red-400">Error: {(accessCheck.error as Error).message}</p>
          )}
        </div>

        <div>
          {result && <AccessResultPanel result={result} />}
          {!result && !accessCheck.isPending && (
            <div className="h-full flex items-center justify-center border border-border-subtle rounded-lg bg-surface-tertiary">
              <p className="text-sm text-gray-500">Upload an image and check access</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AccessResultPanel({ result }: { result: AccessCheckResponse }) {
  const granted = result.access_granted;
  return (
    <div
      className={`rounded-xl border p-5 space-y-3 ${
        granted
          ? 'bg-green-500/5 border-green-500/30'
          : 'bg-red-500/5 border-red-500/30'
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center ${
            granted ? 'bg-green-500/20' : 'bg-red-500/20'
          }`}
        >
          {granted ? (
            <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          ) : (
            <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
        </div>
        <div>
          <h4 className={`text-lg font-bold ${granted ? 'text-green-400' : 'text-red-400'}`}>
            {granted ? 'Access Granted' : 'Access Denied'}
          </h4>
          <p className="text-xs text-gray-400">{result.reason}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <StatCard label="Person" value={result.person_name || 'Unknown'} />
        <StatCard label="Confidence" value={`${(result.confidence * 100).toFixed(1)}%`} />
        <StatCard label="Clearance" value={result.clearance_level || 'N/A'} />
        <StatCard label="Liveness" value={result.liveness_passed ? 'Passed' : 'Failed'} />
      </div>
    </div>
  );
}

// ── Verify Section ──

function VerifySection() {
  const verify = useVerifyIdentity();
  const { preview, base64, inputRef, handleFile, reset } = useImageUpload();
  const [personId, setPersonId] = useState('');
  const [result, setResult] = useState<VerifyResponse | null>(null);

  function handleVerify() {
    if (!base64 || !personId.trim()) return;
    verify.mutate(
      { image_base64: base64, person_id: personId.trim() },
      { onSuccess: (res) => setResult(res) }
    );
  }

  function handleReset() {
    reset();
    setResult(null);
    setPersonId('');
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-300">Identity Verification</h3>
      <p className="text-xs text-gray-500">
        Verify that a face matches a known person ID (1:1 comparison).
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <ImageUploadArea inputRef={inputRef} onFile={handleFile} preview={preview} />
          <div>
            <label className="block text-xs text-gray-400 mb-1">Person ID *</label>
            <input
              type="text"
              value={personId}
              onChange={(e) => setPersonId(e.target.value)}
              placeholder="EMP-001 or person UUID"
              className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-4 py-2.5 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/50"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleVerify}
              disabled={verify.isPending || !base64 || !personId.trim()}
              className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyber-cyan to-helios-600 text-white font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {verify.isPending ? (
                <>
                  <Spinner /> Verifying...
                </>
              ) : (
                'Verify Identity'
              )}
            </button>
            {result && (
              <button
                onClick={handleReset}
                className="px-4 py-2.5 rounded-lg border border-border-subtle text-gray-400 text-sm hover:text-gray-200 transition-colors"
              >
                Reset
              </button>
            )}
          </div>
          {verify.isError && (
            <p className="text-xs text-red-400">Error: {(verify.error as Error).message}</p>
          )}
        </div>

        <div>
          {result && (
            <div
              className={`rounded-xl border p-5 space-y-3 ${
                result.verified
                  ? 'bg-green-500/5 border-green-500/30'
                  : 'bg-red-500/5 border-red-500/30'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    result.verified ? 'bg-green-500/20' : 'bg-red-500/20'
                  }`}
                >
                  {result.verified ? (
                    <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>
                <div>
                  <h4 className={`text-lg font-bold ${result.verified ? 'text-green-400' : 'text-red-400'}`}>
                    {result.verified ? 'Verified' : 'Not Verified'}
                  </h4>
                  <p className="text-xs text-gray-400">{result.message}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <StatCard label="Person" value={result.person_name} />
                <StatCard label="Confidence" value={`${(result.confidence * 100).toFixed(1)}%`} />
              </div>
            </div>
          )}
          {!result && !verify.isPending && (
            <div className="h-full flex items-center justify-center border border-border-subtle rounded-lg bg-surface-tertiary">
              <p className="text-sm text-gray-500">Upload an image and enter a person ID</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Identify Section ──

function IdentifySection() {
  const identify = useIdentifyPerson();
  const { preview, base64, inputRef, handleFile, reset } = useImageUpload();
  const [topK, setTopK] = useState(5);
  const [result, setResult] = useState<IdentifyResponse | null>(null);

  function handleIdentify() {
    if (!base64) return;
    identify.mutate(
      { image_base64: base64, top_k: topK },
      { onSuccess: (res) => setResult(res) }
    );
  }

  function handleReset() {
    reset();
    setResult(null);
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-300">Person Identification</h3>
      <p className="text-xs text-gray-500">
        Identify an unknown person against the enrolled database (1:N search).
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <ImageUploadArea inputRef={inputRef} onFile={handleFile} preview={preview} />
          <div>
            <label className="block text-xs text-gray-400 mb-1">Top K Results</label>
            <input
              type="number"
              value={topK}
              onChange={(e) => setTopK(Math.max(1, Math.min(20, Number(e.target.value))))}
              min={1}
              max={20}
              className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-4 py-2.5 text-sm text-gray-200 focus:outline-none focus:border-cyber-cyan/50"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleIdentify}
              disabled={identify.isPending || !base64}
              className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyber-cyan to-helios-600 text-white font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {identify.isPending ? (
                <>
                  <Spinner /> Identifying...
                </>
              ) : (
                'Identify Person'
              )}
            </button>
            {result && (
              <button
                onClick={handleReset}
                className="px-4 py-2.5 rounded-lg border border-border-subtle text-gray-400 text-sm hover:text-gray-200 transition-colors"
              >
                Reset
              </button>
            )}
          </div>
          {identify.isError && (
            <p className="text-xs text-red-400">Error: {(identify.error as Error).message}</p>
          )}
        </div>

        <div>
          {result && (
            <div className="space-y-3">
              <div
                className={`rounded-lg border p-3 ${
                  result.identified
                    ? 'bg-green-500/5 border-green-500/30'
                    : 'bg-amber-500/5 border-amber-500/30'
                }`}
              >
                <p className={`text-sm font-medium ${result.identified ? 'text-green-400' : 'text-amber-400'}`}>
                  {result.message}
                </p>
              </div>

              {result.matches.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-gray-400 uppercase">Matches</h4>
                  {result.matches.map((match, i) => (
                    <div
                      key={match.person_id}
                      className="flex items-center justify-between p-3 bg-surface-tertiary rounded-lg border border-border-subtle"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-5">#{i + 1}</span>
                        <div>
                          <span className="text-sm font-medium text-gray-200">
                            {match.person_name}
                          </span>
                          <span className="ml-2 text-xs text-gray-500 capitalize">
                            {match.clearance_level}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-surface-secondary rounded-full h-1.5">
                          <div
                            className="bg-cyber-cyan h-1.5 rounded-full"
                            style={{ width: `${match.confidence * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-400 w-10 text-right">
                          {(match.confidence * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {result.matches.length === 0 && (
                <div className="flex items-center justify-center p-8 border border-border-subtle rounded-lg bg-surface-tertiary">
                  <p className="text-sm text-gray-500">No matches found in the database</p>
                </div>
              )}
            </div>
          )}
          {!result && !identify.isPending && (
            <div className="h-full flex items-center justify-center border border-border-subtle rounded-lg bg-surface-tertiary">
              <p className="text-sm text-gray-500">Upload an image to identify</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Liveness Section ──

function LivenessSection() {
  const liveness = useCheckLiveness();
  const { preview, base64, inputRef, handleFile, reset } = useImageUpload();
  const [result, setResult] = useState<LivenessResponse | null>(null);

  function handleCheck() {
    if (!base64) return;
    liveness.mutate(
      { image_base64: base64 },
      { onSuccess: (res) => setResult(res) }
    );
  }

  function handleReset() {
    reset();
    setResult(null);
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-300">Liveness Check</h3>
      <p className="text-xs text-gray-500">
        Anti-spoofing detection to verify the image is a live person, not a photo/mask/deepfake.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <ImageUploadArea inputRef={inputRef} onFile={handleFile} preview={preview} />
          <div className="flex gap-2">
            <button
              onClick={handleCheck}
              disabled={liveness.isPending || !base64}
              className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyber-cyan to-helios-600 text-white font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {liveness.isPending ? (
                <>
                  <Spinner /> Checking...
                </>
              ) : (
                'Check Liveness'
              )}
            </button>
            {result && (
              <button
                onClick={handleReset}
                className="px-4 py-2.5 rounded-lg border border-border-subtle text-gray-400 text-sm hover:text-gray-200 transition-colors"
              >
                Reset
              </button>
            )}
          </div>
          {liveness.isError && (
            <p className="text-xs text-red-400">Error: {(liveness.error as Error).message}</p>
          )}
        </div>

        <div>
          {result && (
            <div
              className={`rounded-xl border p-5 space-y-3 ${
                result.is_live
                  ? 'bg-green-500/5 border-green-500/30'
                  : 'bg-red-500/5 border-red-500/30'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    result.is_live ? 'bg-green-500/20' : 'bg-red-500/20'
                  }`}
                >
                  {result.is_live ? (
                    <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                  )}
                </div>
                <div>
                  <h4 className={`text-lg font-bold ${result.is_live ? 'text-green-400' : 'text-red-400'}`}>
                    {result.is_live ? 'Live Person Detected' : 'Spoofing Detected'}
                  </h4>
                  <p className="text-xs text-gray-400">{result.message}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <StatCard
                  label="Confidence"
                  value={`${(result.confidence * 100).toFixed(1)}%`}
                />
                <StatCard
                  label="Spoofing Type"
                  value={result.spoofing_type || 'None'}
                />
              </div>
            </div>
          )}
          {!result && !liveness.isPending && (
            <div className="h-full flex items-center justify-center border border-border-subtle rounded-lg bg-surface-tertiary">
              <p className="text-sm text-gray-500">Upload an image to check liveness</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Shared Sub-components ──

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-surface-tertiary rounded-lg p-3 border border-border-subtle">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-semibold text-gray-200 mt-0.5 truncate capitalize">{value}</div>
    </div>
  );
}
